import type { Identifier } from "ra-core";

import type {
  MetaAssignmentOutcome,
  MetaFormProject,
  MetaProjectChoice,
  MetaSourceChoice,
  MetaWebhookResult,
} from "../types";

/**
 * Almikia Epic E9 (TASK-049): pure rules of the direccion-only "Leads de Meta" screen. The
 * screen is a plain chronological status list plus a form -> project mapping editor: nothing
 * here counts, ranks or compares advisors, and the raw webhook payload is never read.
 */

/** Page size of the received-webhooks list (bounded, paginated, newest first). */
export const META_EVENTS_PAGE_SIZE = 25;

export const META_WEBHOOK_RESULTS: readonly MetaWebhookResult[] = [
  "received",
  "rejected_signature",
  "processed",
  "ignored",
  "error",
];

export const META_FORM_ID_REQUIRED = "Escribe el id del formulario de Meta";
export const META_FORM_ID_DUPLICATE =
  "Ese formulario de Meta ya tiene un proyecto asignado";
export const META_FORM_PROJECT_REQUIRED = "Elige un proyecto";
export const META_FORM_GENERIC_ERROR =
  "No se pudo guardar el cambio. Inténtalo de nuevo.";

/** i18n key of the Spanish label of a webhook result. */
export const resultLabelKey = (result: MetaWebhookResult): string =>
  `crm.metaLeads.result.${result}`;

/** i18n key of the signature validity text (always shown together with an icon). */
export const signatureLabelKey = (isValid: boolean): string =>
  `crm.metaLeads.signature.${isValid ? "valid" : "invalid"}`;

/** i18n key of the assignment outcome of an ingested lead. */
export const assignmentOutcomeKey = (outcome: MetaAssignmentOutcome): string =>
  `crm.metaLeads.assignment.${outcome}`;

/** i18n key of the project choice of an ingested lead. */
export const projectChoiceKey = (choice: MetaProjectChoice): string =>
  `crm.metaLeads.project_choice.${choice}`;

/**
 * Whether the lead was classified by a lead source mapping or the default; only the first case
 * shows the matched tag, the others read "Sin clasificar".
 */
export const isSourceMatched = (choice: MetaSourceChoice): boolean =>
  choice === "mapped_ad" ||
  choice === "mapped_campaign" ||
  choice === "mapped_form";

/** Builds the `ra-data-postgrest` filter of the list from the result filter ("" = all). */
export const buildEventsFilter = (result: string): Record<string, string> =>
  (META_WEBHOOK_RESULTS as readonly string[]).includes(result)
    ? { result }
    : {};

/** Builds the `event_id@in` filter that loads the ingestions of the visible events. */
export const buildIngestionsFilter = (
  eventIds: readonly Identifier[],
): Record<string, string> => ({
  "event_id@in": `(${eventIds.join(",")})`,
});

export type MetaFormMappingValues = {
  meta_form_id: string;
  project_id: string;
};

export type MetaFormMappingErrors = {
  meta_form_id?: string;
  project_id?: string;
};

/** Normalized form id, the way the database unique index compares it (trimmed). */
export const normalizeFormId = (formId: string): string => formId.trim();

/**
 * Validates a mapping row: a non-empty form id, unique among the existing mappings (the edited
 * row, `selfId`, is excluded), and a chosen project.
 */
export const validateFormMapping = (
  values: MetaFormMappingValues,
  existing: ReadonlyArray<Pick<MetaFormProject, "id" | "meta_form_id">>,
  selfId?: Identifier,
): MetaFormMappingErrors => {
  const errors: MetaFormMappingErrors = {};
  const formId = normalizeFormId(values.meta_form_id);
  if (formId.length === 0) {
    errors.meta_form_id = META_FORM_ID_REQUIRED;
  } else if (
    existing.some(
      (row) =>
        row.id !== selfId && normalizeFormId(row.meta_form_id) === formId,
    )
  ) {
    errors.meta_form_id = META_FORM_ID_DUPLICATE;
  }
  if (values.project_id === "") {
    errors.project_id = META_FORM_PROJECT_REQUIRED;
  }
  return errors;
};

/** Whether the validation found nothing to fix. */
export const isMappingValid = (errors: MetaFormMappingErrors): boolean =>
  !errors.meta_form_id && !errors.project_id;

/** Message shown for a failed save: the Spanish provider/DB message when readable, else generic. */
export const toMetaFormErrorMessage = (error: unknown): string => {
  const message = error instanceof Error ? error.message : "";
  return /duplicate|uq__meta_form_projects/i.test(message)
    ? META_FORM_ID_DUPLICATE
    : /[áéíóúñ¿]|Solo |Ese |Escribe/.test(message)
      ? message
      : META_FORM_GENERIC_ERROR;
};
